// EPOCH 21 PHASE 3: Real-time CSR Validation Interface
// Advanced parsing validation and correction tool for CSRs
// Date: August 1, 2025

import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../supabase/client';
import { useAuthSession } from '../../hooks/useAuthSession';
import { ConversationMessage, ParsingCorrection } from '../../types/lead-responses';
// EPOCH 21 PHASE 3: Import feedback system
import ValidationFeedbackSystem from './ValidationFeedbackSystem';
import { extractEdgeFunctionErrorMessage } from '../../utils/edgeFunctionError';

interface ParsingAnalytics {
  conversation_hash: string;
  total_messages: number;
  user_messages: number;
  lead_messages: number;
  average_confidence: number;
  min_confidence: number;
  max_confidence: number;
  quality_score: number;
  parsing_time_ms: number;
  cache_hit: boolean;
  team_members_used: string[];
  language_detected: 'english' | 'chinese' | 'mixed' | 'unknown';
  potential_issues: QualityIssue[];
  created_at: string;
}

interface QualityIssue {
  type: 'low_confidence' | 'unknown_sender' | 'missing_timestamp' | 'short_content' | 'potential_misattribution';
  message_id: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  confidence_impact: number;
}

interface CSRParsingValidationProps {
  rawConversation: string;
  leadName?: string;
  aliasName?: string; // EPOCH 51: Accept alias name for accurate parsing
  onParsingValidated?: (validatedMessages: ConversationMessage[], corrections: ParsingCorrection[]) => void;
  onClose?: () => void;
  showInModal?: boolean;
}

const CSRParsingValidationInterface: React.FC<CSRParsingValidationProps> = ({
  rawConversation,
  leadName = 'Lead',
  aliasName, // EPOCH 51: Receive alias name
  onParsingValidated,
  onClose,
  showInModal = true
}) => {
  const { session } = useAuthSession();
  const user = session?.user;

  // State management
  const [parsedMessages, setParsedMessages] = useState<ConversationMessage[]>([]);
  const [analytics, setAnalytics] = useState<ParsingAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<ParsingCorrection[]>([]);
  const [validationMode, setValidationMode] = useState<'review' | 'edit'>('review');
  const [conversationHash, setConversationHash] = useState<string>('');
  
  // CSR editing state
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingSender, setEditingSender] = useState<'user' | 'lead'>('lead');
  const [editingSenderName, setEditingSenderName] = useState<string>('');
  
  // EPOCH 51: Track if this is first parse (for better loading message)
  const [isFirstParse, setIsFirstParse] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Processing conversation...');

  // Generate conversation hash (simplified version)
  const generateConversationHash = useCallback(async (content: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(content.trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 8);
  }, []);

  // Parse conversation with analytics using OpenAI
  const parseConversationWithAnalytics = useCallback(async () => {
    if (!rawConversation.trim()) return;
    
    setLoading(true);
    setError(null);
    
    // EPOCH 51: Set appropriate loading message
    if (isFirstParse) {
      setLoadingMessage('Initializing AI parser (first use may take 15-30 seconds)...');
    } else {
      setLoadingMessage('Parsing conversation with AI...');
    }
    
    try {
      console.log('🔍 EPOCH 51: Starting OpenAI CSR validation parsing with alias...');
      console.log('Raw conversation length:', rawConversation?.length);
      console.log('Lead name:', leadName);
      console.log('Alias name:', aliasName); // EPOCH 51: Log alias name
      console.log('First 200 chars of conversation:', rawConversation?.substring(0, 200));
      
      // Use OpenAI conversation analysis function directly
      console.log('Calling Edge Function...');
      console.log('Request payload size:', JSON.stringify({
        conversation_raw: rawConversation,
        lead_name: leadName,
        alias_name: aliasName,
        analysis_type: 'quality_analysis'
      }).length, 'bytes');
      
      const startTime = Date.now();
      
      // EPOCH 51: Add timeout and better error handling
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Edge Function timeout after 25 seconds')), 25000)
      );
      
      const functionPromise = supabase.functions.invoke('ai-conversation-analysis-openai', {
        body: {
          conversation_raw: rawConversation,
          lead_name: leadName,
          alias_name: aliasName, // EPOCH 51: Pass alias name to Edge Function
          analysis_type: 'quality_analysis'
        }
      });
      
      const result = await Promise.race([functionPromise, timeoutPromise]).catch(err => {
        console.error('Edge Function error caught:', err);
        return { data: null, error: err };
      });
      
      const { data, error } = result as any;
      
      const duration = Date.now() - startTime;
      console.log('Edge Function call took:', duration, 'ms');
      
      // EPOCH 51: Enhanced diagnostics for response handling
      if (data) {
        console.log('✅ Edge Function returned data:', {
          success: data.success,
          hasResult: !!data.result,
          messageCount: data.result?.parsed_conversation?.length || 0,
          confidence: data.result?.confidence_score,
          hasMetadata: !!data.metadata,
          processingTime: data.metadata?.processing_time_ms,
          tokenUsage: data.metadata?.token_usage,
          responseSize: JSON.stringify(data).length,
          duration: duration
        });
      }
      
      if (error) {
        console.error('❌ Edge Function error details:', {
          errorType: error.constructor.name,
          message: error.message,
          code: error.code,
          status: error.status,
          details: error.details,
          duration: duration
        });
      }
      
      if (error) {
        console.error('Edge Function error:', error);
        throw error;
      }
      if (data?.error) {
        console.error('Data error:', data.error);
        throw new Error(data.error);
      }
      
      // EPOCH 51: Check for unexpected response format
      if (!data) {
        console.error('No data returned from Edge Function');
        throw new Error('Edge Function returned no data');
      }
      
      if (!data.success) {
        console.error('Edge Function returned success=false:', data);
        throw new Error(data.error || 'OpenAI analysis failed');
      }
      
      if (!data.result) {
        console.error('Edge Function returned no result:', data);
        throw new Error('Edge Function returned success but no result data');
      }
      
      // Extract parsed messages from OpenAI response
      const messages = data.result?.parsed_conversation || [];
      const confidence = data.result?.confidence_score || 0;
      const aiInsights = data.result?.ai_insights;
      
      console.log('📊 Extracted from response:', {
        messageCount: messages.length,
        confidence: confidence,
        hasInsights: !!aiInsights
      });
      
      // Transform OpenAI response to match expected format
      const transformedMessages = messages.map((msg: { sender_type?: string; sender?: string; content?: string; timestamp?: string }, index: number) => ({
        id: `msg-${index}`,
        sender: msg.sender_type === 'lead' ? 'lead' : 'user',
        sender_name: msg.sender || (msg.sender_type === 'lead' ? leadName : 'Unknown'),
        content: msg.content || '',
        timestamp: msg.timestamp || null,
        confidence: confidence,
        detectionMethod: 'openai_analysis'
      }));
      
      setParsedMessages(transformedMessages);
      
      // Generate analytics from OpenAI response
      const hash = await generateConversationHash(rawConversation);
      setConversationHash(hash);
      
      // Create analytics object from OpenAI insights
      const analytics = {
        conversation_hash: hash,
        total_messages: transformedMessages.length,
        user_messages: transformedMessages.filter(m => m.sender === 'user').length,
        lead_messages: transformedMessages.filter(m => m.sender === 'lead').length,
        average_confidence: confidence,
        min_confidence: confidence,
        max_confidence: confidence,
        quality_score: aiInsights?.engagement_score || 75,
        parsing_time_ms: data.metadata?.processing_time_ms || 0,
        cache_hit: false,
        team_members_used: [],
        language_detected: 'english',
        potential_issues: transformedMessages.filter(m => (m.confidence || 0) < 50).map((m) => ({
          type: 'low_confidence',
          message_id: m.id,
          description: `OpenAI confidence below threshold: ${m.confidence}%`,
          severity: 'medium',
          confidence_impact: 50 - (m.confidence || 0)
        })),
        created_at: new Date().toISOString()
      };
      
      setAnalytics(analytics);
      
      console.log('✅ OpenAI parsing successful:', {
        messages: transformedMessages.length,
        confidence: confidence,
        cost: data.metadata?.cost_cents + ' cents',
        processing_time: data.metadata?.processing_time_ms + 'ms'
      });
      
      // EPOCH 51: Mark first parse as complete for better loading messages next time
      setIsFirstParse(false);
      
    } catch (error) {
      console.error('❌ OpenAI CSR validation parsing error:', error);
      console.error('Full error object:', error);
      setError(error.message || 'Failed to parse conversation with OpenAI');

      // Show more detailed error for debugging
      if (error.code === 'FunctionsHttpError') {
        const backendMessage = await extractEdgeFunctionErrorMessage(error, error.message);
        setError(`Edge Function error (${error.status}): ${backendMessage}`);
      } else if (error.code === 'FunctionsRelayError') {
        setError('Edge Function not responding - check if JWT verification is disabled');
      } else if (error.code === 'FunctionsFetchError') {
        setError('Network error calling Edge Function');
      }
    } finally {
      setLoading(false);
    }
  }, [rawConversation, leadName, generateConversationHash]);

  // Initialize parsing on component mount
  useEffect(() => {
    parseConversationWithAnalytics();
  }, [parseConversationWithAnalytics]);

  // Handle message correction
  const handleMessageCorrection = useCallback((messageId: string, field: string, newValue: unknown) => {
    setParsedMessages(prevMessages => 
      prevMessages.map(msg => 
        msg.id === messageId 
          ? { ...msg, [field]: newValue }
          : msg
      )
    );
    
    // Track correction for analytics
    const correction = {
      message_id: messageId,
      field,
      old_value: parsedMessages.find(m => m.id === messageId)?.[field],
      new_value: newValue,
      timestamp: new Date().toISOString(),
      corrected_by: user?.email || 'unknown'
    };
    
    setCorrections(prev => [...prev, correction]);
  }, [parsedMessages, user]);

  // Validate and submit corrections
  const handleValidationComplete = useCallback(() => {
    console.log('✅ EPOCH 21 PHASE 3: Validation completed by CSR');
    console.log('Corrections made:', corrections);
    
    if (onParsingValidated) {
      onParsingValidated(parsedMessages, corrections);
    }
  }, [parsedMessages, corrections, onParsingValidated]);

  // Get confidence color for visual indicators
  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 80) return 'text-green-600 bg-green-50';
    if (confidence >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  // Get severity color for quality issues
  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Render quality issues panel
  const renderQualityIssues = () => {
    if (!analytics?.potential_issues.length) return null;
    
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-yellow-800 mb-2 flex items-center">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Quality Issues Detected ({analytics.potential_issues.length})
        </h4>
        <div className="space-y-2">
          {analytics.potential_issues.map((issue, idx) => (
            <div key={idx} className={`text-xs p-2 rounded border ${getSeverityColor(issue.severity)}`}>
              <div className="font-medium">{issue.type.replace(/_/g, ' ').toUpperCase()}</div>
              <div className="mt-1">{issue.description}</div>
              <div className="mt-1 text-xs opacity-75">Message: {issue.message_id} | Impact: -{issue.confidence_impact}%</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render analytics summary
  const renderAnalyticsSummary = () => {
    if (!analytics) return null;
    
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-3">Parsing Analytics Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <div className="text-blue-600 font-medium">Quality Score</div>
            <div className="text-lg font-bold text-blue-800">{analytics.quality_score}/100</div>
          </div>
          <div>
            <div className="text-blue-600 font-medium">Avg Confidence</div>
            <div className="text-lg font-bold text-blue-800">{analytics.average_confidence}%</div>
          </div>
          <div>
            <div className="text-blue-600 font-medium">Messages</div>
            <div className="text-lg font-bold text-blue-800">{analytics.total_messages}</div>
          </div>
          <div>
            <div className="text-blue-600 font-medium">Language</div>
            <div className="text-lg font-bold text-blue-800 capitalize">{analytics.language_detected}</div>
          </div>
        </div>
      </div>
    );
  };

  // Render message editor
  const renderMessageEditor = (message: ConversationMessage) => {
    const isEditing = editingMessageId === message.id;
    
    return (
      <div key={message.id} className="border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
              message.sender === 'user' 
                ? 'bg-blue-500 text-white' 
                : 'bg-green-500 text-white'
            }`}>
              {message.sender === 'user' ? 'U' : 'L'}
            </div>
            
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <select
                  value={editingSender}
                  onChange={(e) => setEditingSender(e.target.value as 'user' | 'lead')}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                >
                  <option value="lead">Lead</option>
                  <option value="user">User</option>
                </select>
                <input
                  type="text"
                  value={editingSenderName}
                  onChange={(e) => setEditingSenderName(e.target.value)}
                  placeholder="Sender name"
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                />
              </div>
            ) : (
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {message.sender_name || 'Unknown'}
                </div>
                <div className="text-xs text-gray-500">
                  {message.sender === 'user' ? 'User Response' : 'Lead Message'}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {message.confidence && (
              <span className={`px-2 py-1 text-xs font-medium rounded ${getConfidenceColor(message.confidence)}`}>
                {message.confidence}% confidence
              </span>
            )}
            
            {isEditing ? (
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    handleMessageCorrection(message.id, 'sender', editingSender);
                    handleMessageCorrection(message.id, 'sender_name', editingSenderName);
                    setEditingMessageId(null);
                  }}
                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingMessageId(null)}
                  className="px-2 py-1 bg-gray-400 text-white text-xs rounded hover:bg-gray-500"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setEditingMessageId(message.id);
                  setEditingSender(message.sender);
                  setEditingSenderName(message.sender_name || '');
                }}
                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                Edit
              </button>
            )}
          </div>
        </div>
        
        <div className="bg-gray-50 rounded p-3 mb-2">
          <div className="text-sm text-gray-800 whitespace-pre-wrap">
            {message.content}
          </div>
        </div>
        
        {message.detectionMethod && (
          <div className="text-xs text-gray-500">
            Detection: {message.detectionMethod} | {message.timestamp && `Time: ${message.timestamp}`}
          </div>
        )}
      </div>
    );
  };

  const content = (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">CSR Parsing Validation</h2>
          <p className="text-sm text-gray-600 mt-1">
            Review and validate conversation parsing accuracy with real-time analytics
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setValidationMode('review')}
              className={`px-3 py-1 text-sm rounded ${
                validationMode === 'review' 
                  ? 'bg-white text-gray-900 shadow' 
                  : 'text-gray-600'
              }`}
            >
              Review
            </button>
            <button
              onClick={() => setValidationMode('edit')}
              className={`px-3 py-1 text-sm rounded ${
                validationMode === 'edit' 
                  ? 'bg-white text-gray-900 shadow' 
                  : 'text-gray-600'
              }`}
            >
              Edit
            </button>
          </div>
          
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">{loadingMessage}</p>
          {isFirstParse && (
            <p className="text-xs text-gray-500 mt-1">Please wait while we initialize the AI system...</p>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="text-red-800 font-medium">Parsing Error</div>
          <div className="text-red-700 text-sm mt-1">{error}</div>
        </div>
      )}

      {!loading && !error && (
        <>
          {renderAnalyticsSummary()}
          {renderQualityIssues()}
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Parsed Messages ({parsedMessages.length})
              </h3>
              
              {corrections.length > 0 && (
                <div className="text-sm text-blue-600">
                  {corrections.length} correction(s) made
                </div>
              )}
            </div>
            
            {parsedMessages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No messages detected in conversation
              </div>
            ) : (
              <div className="space-y-4">
                {parsedMessages.map(renderMessageEditor)}
              </div>
            )}
            
            {parsedMessages.length > 0 && (
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => parseConversationWithAnalytics()}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Re-parse
                </button>
                <button
                  onClick={handleValidationComplete}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Validate & Save ({corrections.length} corrections)
                </button>
              </div>
            )}
            
            {/* EPOCH 21 PHASE 3: Validation Feedback System */}
            {parsedMessages.length > 0 && conversationHash && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <ValidationFeedbackSystem
                  conversationHash={conversationHash}
                  messages={parsedMessages}
                  onFeedbackSubmitted={(feedback) => {
                    console.log('📝 Validation feedback submitted:', feedback);
                  }}
                  showInline={false}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (showInModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          {content}
        </div>
      </div>
    );
  }

  return content;
};

export default CSRParsingValidationInterface;